import { Link } from "react-router-dom";
import type { NewsArticle } from "@/mocks/articles";
import { formatCount } from "@/mocks/articles";

export function catPill(cat: string): string {
  switch (cat) {
    case "yule":
      return "bg-primary-100 text-primary-700";
    case "zongyi":
      return "bg-accent-100 text-accent-700";
    case "shehui":
      return "bg-secondary-100 text-secondary-800";
    case "wangluo":
      return "bg-background-200 text-foreground-800";
    default:
      return "bg-background-200 text-foreground-800";
  }
}

export function CategoryBadge({ cat, catName, dark }: { cat: string; catName: string; dark?: boolean }) {
  const pill = dark ? "bg-background-50/20 text-background-50" : catPill(cat);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${pill}`}
    >
      {catName}
    </span>
  );
}

/* 纵向卡片 */
export function ArticleCard({ article, index }: { article: NewsArticle; index?: number }) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 transition-colors hover:border-primary-200 hover:bg-background-100/60">
      <Link to={`/article/${article.slug}`} className="relative block overflow-hidden" aria-label={article.title}>
        <img
          src={article.cover}
          alt={`${article.title}封面图`}
          title={`${article.title} - 吃瓜现场`}
          className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3">
          <CategoryBadge cat={article.cat} catName={article.catName} dark />
        </span>
        {typeof index === "number" && index < 3 && (
          <span className="absolute right-3 top-3 flex h-7 min-w-7 items-center justify-center rounded-full bg-primary-500 px-2 font-heading text-sm font-bold text-background-50">
            {index + 1}
          </span>
        )}
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
          <Link to={`/article/${article.slug}`} className="line-clamp-2">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-700 hidden sm:block">
          {article.excerpt}
        </p>
        <div className="mt-auto flex items-center justify-between pt-3 text-xs text-foreground-600">
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-calendar-line text-primary-400" aria-hidden="true"></i>
            {article.date}
          </span>
          <span className="inline-flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true"></i>
              {formatCount(article.views)}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-chat-3-line" aria-hidden="true"></i>
              {formatCount(article.comments)}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}

/* 横向列表条目 */
export function ArticleRow({ article, rank, showExcerpt }: { article: NewsArticle; rank?: number; showExcerpt?: boolean }) {
  return (
    <article className="group flex gap-4 border-b border-background-200/70 py-4 last:border-0">
      <Link to={`/article/${article.slug}`} className="block shrink-0 overflow-hidden rounded-xl" aria-label={article.title}>
        <img
          src={article.cover}
          alt={`${article.title}配图`}
          title={`${article.title} - 吃瓜现场`}
          className="h-20 w-28 object-cover transition-transform duration-500 group-hover:scale-105 sm:h-24 sm:w-36"
        />
      </Link>
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        {typeof rank === "number" && (
          <span className="mb-1 font-heading text-2xl font-black leading-none text-primary-400">{rank}</span>
        )}
        <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 sm:text-base">
          <Link to={`/article/${article.slug}`}>{article.title}</Link>
        </h3>
        {showExcerpt && (
          <p className="mt-1.5 line-clamp-1 text-xs text-foreground-600">{article.excerpt}</p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-foreground-600">
          <CategoryBadge cat={article.cat} catName={article.catName} />
          <span className="inline-flex items-center gap-1">
            <i className="ri-eye-line" aria-hidden="true"></i>
            {formatCount(article.views)}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-chat-3-line" aria-hidden="true"></i>
            {formatCount(article.comments)}
          </span>
        </div>
      </div>
    </article>
  );
}